import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Menu, MenuItem, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from "react-router-dom";
import { USE_FAKE_DATA } from "../../config/appConfig";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";
import ActionDialog from "../../components/ActionDialog";

export type BookingStatus = "PENDING" | "CONFIRMED" | "ONGOING" | "COMPLETED" | "CANCELLED" | "HOLD";

export type PaymentStatus = "UNPAID" | "PARTIAL" | "PAID" | "FAILED";

type Booking = {
  id: string;
  bookingId: string;
  customer: string;
  project: string;
  product: string;
  dateTime: string; // ISO
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  rfqId?: string;
  schedule?: string; // human readable
  vehicle?: string; // vehicle assignment (ERP)
};

const FAKE_BOOKINGS: Booking[] = [
  { id: 'b1', bookingId: 'BK-10001', customer: 'Acme LLC', project: 'Marina Tower', product: 'ReadyMix M25', dateTime: '2025-11-12 10:00', status: 'PENDING', paymentStatus: 'UNPAID', rfqId: 'r1', schedule: 'Nov 12, 10:00 - 12:00', vehicle: '-' },
  { id: 'b2', bookingId: 'BK-10002', customer: 'BuildCo', project: 'Expo Villas', product: 'ReadyMix M30', dateTime: '2025-11-13 09:30', status: 'CONFIRMED', paymentStatus: 'PAID', rfqId: 'r2', schedule: 'Nov 13, 09:30 - 11:00', vehicle: 'Truck-07' },
  { id: 'b3', bookingId: 'BK-10003', customer: 'Skyline', project: 'Harbour Point', product: 'ReadyMix M20', dateTime: '2025-11-11 15:00', status: 'ONGOING', paymentStatus: 'PARTIAL', schedule: 'Nov 11, 15:00 - 17:00', vehicle: 'Truck-12' },
  { id: 'b4', bookingId: 'BK-10004', customer: 'Acme LLC', project: 'Marina Tower', product: 'ReadyMix M40', dateTime: '2025-11-10 08:00', status: 'COMPLETED', paymentStatus: 'PAID', rfqId: 'r4', schedule: 'Nov 10, 08:00 - 10:00', vehicle: 'Truck-03' },
  { id: 'b5', bookingId: 'BK-10005', customer: 'BuildCo', project: 'Expo Villas', product: 'ReadyMix M35', dateTime: '2025-11-09 17:00', status: 'CANCELLED', paymentStatus: 'FAILED', rfqId: 'r5', schedule: 'Nov 09, 17:00 - 18:30', vehicle: '-' },
];

const BookingListPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Exclude<BookingStatus, 'HOLD'>>('PENDING');
  const [searchTerm, setSearchTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [items, setItems] = useState<Booking[]>(USE_FAKE_DATA ? FAKE_BOOKINGS : []);

  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);
  const [actionForId, setActionForId] = useState<string | null>(null);

  const [openReject, setOpenReject] = useState(false);
  const [openPropose, setOpenPropose] = useState(false);
  const [openHold, setOpenHold] = useState(false);
  const [reason, setReason] = useState('');
  const [newTime, setNewTime] = useState('');

  const filtered = useMemo(() => {
    let data = [...items];
    data = data.filter((b) => b.status === tab);
    const term = (debounced || '').toLowerCase();
    if (term) {
      data = data.filter((b) =>
        b.bookingId.toLowerCase().includes(term) ||
        b.customer.toLowerCase().includes(term) ||
        b.project.toLowerCase().includes(term) ||
        b.product.toLowerCase().includes(term)
      );
    }
    return data;
  }, [items, tab, debounced]);

  const total = filtered.length;
  const start = (page - 1) * limit;
  const end = start + limit;
  const pageItems = filtered.slice(start, end);
  const totalPages = Math.max(1, Math.ceil(total / (limit || 10)));

  useEffect(() => { setPage(1); }, [tab, debounced]);

  const handleOpenActions = (e: React.MouseEvent<HTMLElement>, id: string) => {
    setActionAnchor(e.currentTarget);
    setActionForId(id);
  };
  const handleCloseActions = () => { setActionAnchor(null); setActionForId(null); };

  // Actions (UI-only)
  const accept = (id: string) => {
    if (!USE_FAKE_DATA) return;
    setItems((prev) => prev.map((b) => b.id === id ? { ...b, status: 'CONFIRMED' } : b));
  };
  const reject = (id: string) => { setActionForId(id); setReason(''); setOpenReject(true); };
  const saveReject = () => {
    if (!USE_FAKE_DATA || !actionForId) return;
    setItems((prev) => prev.map((b) => b.id === actionForId ? { ...b, status: 'CANCELLED' } : b));
    setOpenReject(false);
  };
  const propose = (id: string) => { setActionForId(id); setNewTime(''); setOpenPropose(true); };
  const savePropose = () => { setOpenPropose(false); };
  const hold = (id: string) => { setActionForId(id); setOpenHold(true); };
  const saveHold = () => {
    if (!USE_FAKE_DATA || !actionForId) return;
    setItems((prev) => prev.map((b) => b.id === actionForId ? { ...b, status: 'HOLD' } : b));
    setOpenHold(false);
  };
  const cancel = (id: string) => {
    if (!USE_FAKE_DATA) return;
    setItems((prev) => prev.map((b) => b.id === id ? { ...b, status: 'CANCELLED' } : b));
  };

  return (
    <>
      <div className="main_title">
        <h1>Booking Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Bookings
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: '100%', mb: 1 }}>
              <Tabs
                value={tab === 'PENDING' ? 0 : tab === 'CONFIRMED' ? 1 : tab === 'ONGOING' ? 2 : tab === 'COMPLETED' ? 3 : 4}
                onChange={(_, i) => setTab(i === 0 ? 'PENDING' : i === 1 ? 'CONFIRMED' : i === 2 ? 'ONGOING' : i === 3 ? 'COMPLETED' : 'CANCELLED')}
                className="custom_tabs_links"
              >
                <Tab label="Pending" />
                <Tab label="Confirmed" />
                <Tab label="Ongoing" />
                <Tab label="Completed" />
                <Tab label="Cancelled" />
              </Tabs>
            </Box>
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search by booking, customer, project, product..."
              setDebouncedSearchTerm={setDebounced}
              value={searchTerm}
              onCross={() => setSearchTerm('')}
              onChange={(val: any) => { if (isValidInput(val.target.value)) setSearchTerm(val.target.value); }}
            />
          </div>
          <div className="right_s">
            <Button className="btn btn_primary" onClick={() => setItems(FAKE_BOOKINGS)}>Reset</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.length ? pageItems.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.bookingId}</TableCell>
                  <TableCell>{b.customer}</TableCell>
                  <TableCell>{b.project}</TableCell>
                  <TableCell>{b.product}</TableCell>
                  <TableCell>{b.dateTime}</TableCell>
                  <TableCell>{b.paymentStatus}</TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleOpenActions(e, b.id)}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={actionAnchor}
                      open={Boolean(actionAnchor) && actionForId === b.id}
                      onClose={handleCloseActions}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => { navigate(`/bookings/${b.id}`, { state: { booking: b } }); handleCloseActions(); }}>View</MenuItem>
                      <MenuItem onClick={() => { accept(b.id); handleCloseActions(); }}>Accept</MenuItem>
                      <MenuItem onClick={() => { reject(b.id); handleCloseActions(); }}>Reject</MenuItem>
                      <MenuItem onClick={() => { propose(b.id); handleCloseActions(); }}>Propose New Time</MenuItem>
                      <MenuItem onClick={() => { hold(b.id); handleCloseActions(); }}>Hold</MenuItem>
                      <MenuItem onClick={() => { cancel(b.id); handleCloseActions(); }}>Cancel</MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7}>No Bookings found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination module={pageItems} page={page} setPage={setPage} limit={limit} setLimit={setLimit} onPageChange={setPage} totalPages={totalPages} />
        </TableContainer>
      </div>

      {/* Reject Dialog */}
      <ActionDialog
        open={openReject}
        onClose={() => setOpenReject(false)}
        title="Reject Booking"
        helperText="Kindly capture why this booking is being rejected."
        confirmLabel="Reject"
        confirmDisabled={!reason.trim()}
        onConfirm={saveReject}
        confirmButtonProps={{
          className: "btn btn_secondary",
        }}
      >
        <TextField
          label="Reason"
          placeholder="Enter rejection reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          sx={{
            '& textarea': { 
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>

      {/* Propose New Time Dialog */}
      <ActionDialog
        open={openPropose}
        onClose={() => setOpenPropose(false)}
        title="Propose New Time"
        helperText="Send an alternate slot to the customer for confirmation."
        confirmLabel="Send"
        confirmDisabled={!newTime}
        onConfirm={savePropose}
        confirmButtonProps={{
          className: "btn btn_primary",
        }}
      >
        <TextField
          type="datetime-local"
          label="New Date/Time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{
            '& textarea': { 
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>

      {/* Hold Dialog */}
      <ActionDialog
        open={openHold}
        onClose={() => setOpenHold(false)}
        title="Hold Booking"
        helperText="Pause this booking while you gather more information."
        confirmLabel="Hold"
        onConfirm={saveHold}
        confirmButtonProps={{
          className: "btn btn_outline",
        }}
      >
        <Box sx={{ p: 2, borderRadius: 2, background: '#f8fafc', color: '#475569', fontSize: '0.9rem' }}>
          Booking will be paused until further action.
        </Box>
      </ActionDialog>
    </>
  );
};

export default BookingListPage;
